import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Upload, Download, GitMerge, Undo2, Plus, Star, Archive } from "lucide-react";
import api from "@/lib/api";
import { toCsv, downloadCsv } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field, Panel, DelBtn } from "@/pages/admin/adminShared";
import { findDuplicatePairs } from "@/lib/dedupe";

function SeasonsManager({ seasons = [], currentSeason, onCreateSeason, onArchiveSeason, onSetDefaultSeason, onDeleteSeason }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!f.name.trim()) { toast.error("Enter a season name"); return; }
    setBusy(true);
    const ok = await onCreateSeason(f);
    setBusy(false);
    if (ok) { setF({ name: "", start_date: "", end_date: "" }); setOpen(false); }
  };
  const fmtRange = (s) => [s.start_date, s.end_date].filter(Boolean).join(" → ");
  return (
    <div className="bg-navy-paper border border-gold/30 p-5" data-testid="seasons-manager">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-gold text-xs uppercase tracking-widest">Seasons</div>
          <p className="text-slate-400 text-xs mt-0.5">Current default: <span className="text-white">{currentSeason || "— not set —"}</span>. New events &amp; results default to it.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs shrink-0" data-testid="create-season-btn"><Plus className="w-4 h-4 mr-1" /> Create New Season</Button>
      </div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {seasons.map((s) => (
          <div key={s.id || s.name} className={`flex items-center justify-between gap-2 border p-2.5 ${s.is_default ? "border-gold/50" : "border-slate-800"}`} data-testid={`season-row-${s.name}`}>
            <div className="min-w-0">
              <div className="text-white text-sm truncate">{s.name} {s.is_default && <span className="text-gold text-[10px] uppercase ml-1">· Default</span>} {s.archived && <span className="text-slate-500 text-[10px] uppercase ml-1">· Archived</span>}</div>
              {fmtRange(s) && <div className="text-slate-500 text-xs">{fmtRange(s)}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {s.id && !s.is_default && <button onClick={() => onSetDefaultSeason(s.id)} className="text-slate-400 hover:text-gold" title="Set as default season" data-testid={`season-default-${s.name}`}><Star className="w-4 h-4" /></button>}
              {s.id && <button onClick={() => onArchiveSeason(s.id)} className={`${s.archived ? "text-gold" : "text-slate-400 hover:text-gold"}`} title={s.archived ? "Unarchive" : "Archive"} data-testid={`season-archive-${s.name}`}><Archive className="w-4 h-4" /></button>}
              {s.id && <DelBtn onClick={() => onDeleteSeason(s.id)} />}
            </div>
          </div>
        ))}
        {seasons.length === 0 && <p className="text-slate-500 text-xs py-3 text-center">No seasons defined yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-paper border-slate-800 rounded-none" data-testid="create-season-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-white">Create New Season</DialogTitle></DialogHeader>
          <p className="text-slate-400 text-sm">This does not archive the current season — manage archiving and the default separately below.</p>
          <div className="space-y-3">
            <div><Label className="text-slate-300 text-xs">Season Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. 2027 Championship Season" className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="season-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-xs">Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="season-start-input" /></div>
              <div><Label className="text-slate-300 text-xs">End Date</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="season-end-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={busy} className="rounded-none bg-gold text-navy hover:bg-gold-light text-xs uppercase tracking-widest font-bold" data-testid="season-create-confirm">{busy ? "Creating…" : "Create Season"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExportTools({ items }) {
  const seasons = [...new Set(items.map((i) => i.season).filter(Boolean))].sort().reverse();
  const [season, setSeason] = useState("all");
  const exportResults = () => {
    const rows = (season === "all" ? items : items.filter((r) => r.season === season)).slice().sort((a, b) => (a.event_title || "").localeCompare(b.event_title || "") || a.place - b.place);
    if (!rows.length) { toast.error("No results to export for this selection"); return; }
    const headers = [
      { label: "Place", key: "place" }, { label: "Player", key: "player_name" },
      { label: "Event", key: "event_title" }, { label: "Venue", key: "venue" },
      { label: "Season", key: "season" }, { label: "Points", key: "points" },
      { label: "Payout", key: "payout" }, { label: "Date", key: "date" },
    ];
    downloadCsv(`results-${season === "all" ? "all-seasons" : season}.csv`.replace(/\s+/g, "-"), toCsv(rows, headers));
  };
  const exportLeaderboard = async () => {
    try {
      const { data } = await api.get(`/leaderboards?season=${encodeURIComponent(season)}`);
      if (!data.length) { toast.error("No standings to export"); return; }
      const headers = [
        { label: "Rank", key: "rank" }, { label: "Player", key: "player_name" },
        { label: "Points", key: "points" }, { label: "Final Tables", key: "final_tables" },
        { label: "Played", key: "events_played" }, { label: "Wins", key: "wins" },
        { label: "Payouts", key: "total_payout" }, { label: "Qualifier", get: (r) => (r.qualifier ? "Yes" : "") },
      ];
      downloadCsv(`leaderboard-${season === "all" ? "overall" : season}.csv`.replace(/\s+/g, "-"), toCsv(data, headers));
    } catch (e) { toast.error("Could not export leaderboard"); }
  };
  return (
    <div className="bg-navy-paper border border-slate-800 p-5" data-testid="export-tools">
      <div className="text-gold text-xs uppercase tracking-widest mb-2 flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</div>
      <select value={season} onChange={(e) => setSeason(e.target.value)} className="w-full rounded-none bg-navy border border-slate-700 text-slate-200 text-sm p-2 mb-3" data-testid="export-season">
        <option value="all">All seasons</option>
        {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="flex gap-2">
        <Button onClick={exportResults} variant="outline" className="flex-1 rounded-none border-slate-700 text-slate-300 hover:bg-white/5 text-xs uppercase tracking-widest" data-testid="export-results">Results</Button>
        <Button onClick={exportLeaderboard} variant="outline" className="flex-1 rounded-none border-slate-700 text-slate-300 hover:bg-white/5 text-xs uppercase tracking-widest" data-testid="export-leaderboard">Leaderboard</Button>
      </div>
    </div>
  );
}

function PlayerMerge({ onMerge, onUndo }) {
  const [names, setNames] = useState([]);
  const [log, setLog] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = () => {
    api.get("/admin/player-names").then((r) => setNames(r.data)).catch(() => {});
    api.get("/admin/merge-log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const go = async () => {
    if (!from || !to) { toast.error("Pick both names"); return; }
    if (from === to) { toast.error("Choose two different names"); return; }
    if (!window.confirm(`Merge "${from}" into "${to}"? All of "${from}"'s results will be reassigned to "${to}". You can undo this from the history below.`)) return;
    const ok = await onMerge(from, to);
    if (ok) { setFrom(""); setTo(""); load(); }
  };
  const undo = async (id) => {
    if (!window.confirm("Undo this merge and restore the original player name?")) return;
    const ok = await onUndo(id);
    if (ok) load();
  };
  const fromCount = names.find((n) => n.name === from)?.count || 0;
  const toCount = names.find((n) => n.name === to)?.count || 0;
  const suggestions = findDuplicatePairs(names);
  return (
    <div className="bg-navy-paper border border-slate-800 p-5" data-testid="player-merge">
      <div className="text-gold text-xs uppercase tracking-widest mb-2 flex items-center gap-2"><GitMerge className="w-4 h-4" /> Merge Duplicate Players</div>
      {suggestions.length > 0 && (
        <div className="mb-3" data-testid="merge-suggestions">
          <div className="text-slate-500 text-[10px] uppercase tracking-widest mb-1.5">Possible duplicates</div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map(([a, b], i) => (
              <button key={`${a.name}|${b.name}`} onClick={() => { setFrom(a.count <= b.count ? a.name : b.name); setTo(a.count <= b.count ? b.name : a.name); }}
                className="text-[11px] border border-gold/30 text-slate-300 hover:border-gold hover:text-gold px-2 py-1 transition-colors" data-testid={`merge-suggestion-${i}`}>
                {a.name} ↔ {b.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <select value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none bg-navy border border-slate-700 text-slate-200 text-sm p-2" data-testid="merge-from">
          <option value="">Merge from…</option>
          {names.map((n) => <option key={n.name} value={n.name}>{n.name} ({n.count})</option>)}
        </select>
        <select value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none bg-navy border border-slate-700 text-slate-200 text-sm p-2" data-testid="merge-to">
          <option value="">Into…</option>
          {names.map((n) => <option key={n.name} value={n.name}>{n.name} ({n.count})</option>)}
        </select>
      </div>
      {from && to && from !== to && (
        <p className="text-slate-400 text-[11px] mb-2" data-testid="merge-preview">
          <span className="text-gold font-semibold">{fromCount}</span> result{fromCount === 1 ? "" : "s"} from <span className="text-white">{from}</span> will move into <span className="text-white">{to}</span> ({toCount}). New total: <span className="text-gold font-semibold">{fromCount + toCount}</span>.
        </p>
      )}
      <Button onClick={go} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="merge-submit">Merge Players</Button>
      {log.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3" data-testid="merge-log">
          <div className="text-slate-500 text-[10px] uppercase tracking-widest mb-2">Recent merges</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {log.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-xs" data-testid={`merge-log-${m.id}`}>
                <span className="text-slate-300 truncate pr-2"><span className="text-slate-500">{m.from_name}</span> → <span className="text-white">{m.to_name}</span> <span className="text-slate-600">({m.merged})</span></span>
                {m.undone ? <span className="text-slate-600 uppercase text-[10px] tracking-widest flex-shrink-0">Undone</span>
                  : <button onClick={() => undo(m.id)} className="text-gold hover:underline uppercase text-[10px] tracking-widest flex items-center gap-1 flex-shrink-0" data-testid={`undo-merge-${m.id}`}><Undo2 className="w-3 h-3" /> Undo</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultsTable({ items, editingId, onEdit, onDelete }) {
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-left text-sm min-w-[600px]">
        <thead className="bg-secondary text-gold text-xs uppercase"><tr><th className="px-4 py-2">Player</th><th className="px-4 py-2">Event</th><th className="px-4 py-2">Venue</th><th className="px-4 py-2">Place</th><th className="px-4 py-2">Pts</th><th className="px-4 py-2 text-right"></th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className={`border-t border-white/5 ${editingId === r.id ? "bg-gold/10" : ""}`} data-testid={`admin-result-${r.id}`}>
              <td className="px-4 py-2 text-white">{r.player_name}</td>
              <td className="px-4 py-2 text-slate-400">{r.event_title}</td>
              <td className="px-4 py-2 text-slate-400">{r.venue}</td>
              <td className="px-4 py-2 text-gold">{r.place}</td>
              <td className="px-4 py-2 text-slate-300">{r.points}</td>
              <td className="px-4 py-2 text-right">
                <span className="inline-flex items-center gap-3">
                  <button onClick={() => onEdit(r)} className="text-slate-500 hover:text-gold transition-colors" data-testid={`admin-edit-result-${r.id}`}><Pencil className="w-4 h-4" /></button>
                  <DelBtn onClick={() => onDelete(r.id)} />
                </span>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No results recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BulkEdit({ items, onBulkUpdate, onDelete }) {
  const eventTitles = [...new Set(items.map((i) => i.event_title).filter(Boolean))].sort();
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!selected) { setRows([]); return; }
    setRows(items.filter((r) => r.event_title === selected).sort((a, b) => a.place - b.place).map((r) => ({ ...r })));
  }, [selected, items]);
  const setRow = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const save = async () => {
    if (!rows.length) return;
    setSaving(true);
    const payload = rows.map((r) => ({ id: r.id, event_title: r.event_title, player_name: r.player_name, venue: r.venue, season: r.season, place: Number(r.place), points: Number(r.points), payout: Number(r.payout), date: r.date || "", qualifier: !!r.qualifier }));
    await onBulkUpdate(payload);
    setSaving(false);
  };
  return (
    <div className="mt-6" data-testid="bulk-edit">
      <div className="bg-navy-paper border border-gold/30 p-5 mb-4">
        <Label className="text-slate-300 text-xs">Pick an event to edit its whole finishing order at once</Label>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="w-full mt-1 rounded-none bg-navy border border-slate-700 text-slate-200 text-sm p-2" data-testid="edit-night-event">
          <option value="">Select an event…</option>
          {eventTitles.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[2fr_70px_80px_90px_40px] gap-2 text-gold text-[10px] uppercase tracking-widest px-1">
            <span>Player</span><span>Place</span><span>Points</span><span>Payout</span><span></span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-2 sm:grid-cols-[2fr_70px_80px_90px_40px] gap-2 items-center" data-testid={`edit-row-${r.id}`}>
              <Input value={r.player_name} onChange={(e) => setRow(r.id, "player_name", e.target.value)} className="rounded-none bg-navy border-slate-700 text-sm" data-testid={`edit-player-${r.id}`} />
              <Input type="number" value={r.place} onChange={(e) => setRow(r.id, "place", e.target.value)} className="rounded-none bg-navy border-slate-700 text-sm" data-testid={`edit-place-${r.id}`} />
              <Input type="number" value={r.points} onChange={(e) => setRow(r.id, "points", e.target.value)} className="rounded-none bg-navy border-slate-700 text-sm" data-testid={`edit-points-${r.id}`} />
              <Input type="number" value={r.payout} onChange={(e) => setRow(r.id, "payout", e.target.value)} className="rounded-none bg-navy border-slate-700 text-sm" data-testid={`edit-payout-${r.id}`} />
              <div className="flex justify-end"><DelBtn onClick={() => onDelete(r.id)} /></div>
            </div>
          ))}
          <Button onClick={save} disabled={saving} className="mt-3 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="edit-save-all">{saving ? "Saving…" : "Save all changes"}</Button>
        </div>
      )}
      {selected && rows.length === 0 && <p className="text-slate-500 text-center py-10">No results for that event.</p>}
    </div>
  );
}

export default function ResultsAdmin({ items, events, formula = { places: {}, default_points: 10 }, currentSeason, seasons = [], onCreate, onUpdate, onDelete, onBulk, onCreateSeason, onArchiveSeason, onSetDefaultSeason, onDeleteSeason, onMerge, onBulkUpdate, onUndoMerge }) {
  const pointsFor = (place) => (formula.places?.[String(place)] ?? formula.default_points ?? 0);
  const empty = { event_title: "", player_name: "", venue: "", season: currentSeason || "2026 Championship Season", place: 1, points: 0, payout: 0, date: "", qualifier: false };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [mode, setMode] = useState("single");
  const [bulk, setBulk] = useState({ event_title: "", venue: "", season: currentSeason || "2026 Championship Season", date: "", text: "" });
  useEffect(() => { setF((prev) => (prev.player_name === "" && !editingId ? { ...prev, points: pointsFor(prev.place) } : prev)); }, [formula]);
  useEffect(() => {
    if (currentSeason) {
      setF((p) => (p.player_name === "" && !editingId ? { ...p, season: currentSeason } : p));
      setBulk((b) => (b.text === "" ? { ...b, season: currentSeason } : b));
    }
  }, [currentSeason]);
  const setPlace = (place) => setF((prev) => ({ ...prev, place, ...(editingId ? {} : { points: pointsFor(place) }) }));
  const startEdit = (r) => { setMode("single"); setEditingId(r.id); setF({ ...empty, ...r }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setF({ ...empty, season: currentSeason || empty.season }); };
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, place: Number(f.place), points: Number(f.points), payout: Number(f.payout) };
    const ok = editingId ? await onUpdate(editingId, body) : await onCreate(body);
    if (ok) { if (editingId) cancel(); else setF({ ...empty, event_title: f.event_title, venue: f.venue, season: f.season }); }
  };

  const rows = bulk.text.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => {
    const parts = line.split(/[,\t]/).map((p) => p.trim());
    const place = i + 1;
    return {
      place,
      player_name: parts[0],
      points: parts[1] ? Number(parts[1]) : pointsFor(place),
      payout: parts[2] ? Number(parts[2]) : 0,
      qualifier: false,
    };
  });
  const submitBulk = async (e) => {
    e.preventDefault();
    if (!bulk.venue.trim()) { toast.error("Venue is required"); return; }
    if (rows.length === 0) { toast.error("Paste at least one player"); return; }
    const ok = await onBulk({ event_title: bulk.event_title, venue: bulk.venue, season: bulk.season, date: bulk.date, rows });
    if (ok) setBulk((b) => ({ ...b, text: "" }));
  };

  const onCsv = (file) => {
    if (file.size > 2 * 1024 * 1024) { toast.error("That file is too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (!lines.length) { toast.error("That file looks empty"); return; }
      const delim = lines[0].includes("\t") ? "\t" : ",";
      let grid = lines.map((l) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, "")));
      const header = grid[0].map((c) => c.toLowerCase());
      const headerish = header.some((c) => /name|player|place|rank|point|payout|prize/.test(c));
      let nameIdx = 0, ptsIdx = -1, payIdx = -1, placeIdx = -1;
      if (headerish) {
        header.forEach((h, i) => {
          if (/name|player/.test(h)) nameIdx = i;
          else if (/point/.test(h)) ptsIdx = i;
          else if (/payout|prize|winning|\$/.test(h)) payIdx = i;
          else if (/place|rank|pos|finish/.test(h)) placeIdx = i;
        });
        grid = grid.slice(1);
      } else if (grid[0].length >= 2 && grid.every((r) => !isNaN(Number(r[0])) && r[0] !== "")) {
        placeIdx = 0; nameIdx = 1; ptsIdx = 2; payIdx = 3;
      } else {
        nameIdx = 0; ptsIdx = 1; payIdx = 2;
      }
      if (placeIdx >= 0) grid.sort((a, b) => Number(a[placeIdx]) - Number(b[placeIdx]));
      const clean = (v) => (v || "").replace(/[^0-9.]/g, "");
      const out = grid.filter((r) => r[nameIdx]).map((r) => {
        const parts = [r[nameIdx]];
        const pts = ptsIdx >= 0 ? clean(r[ptsIdx]) : "";
        const pay = payIdx >= 0 ? clean(r[payIdx]) : "";
        if (pts) parts.push(pts); else if (pay) parts.push("");
        if (pay) parts.push(pay);
        return parts.join(", ");
      }).join("\n");
      if (!out) { toast.error("Couldn't find any player rows in that file"); return; }
      setBulk((b) => ({ ...b, text: out }));
      toast.success(`Loaded ${out.split("\n").length} rows from file`);
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <SeasonsManager seasons={seasons} currentSeason={currentSeason} onCreateSeason={onCreateSeason} onArchiveSeason={onArchiveSeason} onSetDefaultSeason={onSetDefaultSeason} onDeleteSeason={onDeleteSeason} />
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <ExportTools items={items} />
        <PlayerMerge onMerge={onMerge} onUndo={onUndoMerge} />
      </div>
      <div className="flex gap-2 mt-6" data-testid="results-mode-toggle">
        {[["single", "Single Result"], ["bulk", "Bulk Paste"], ["edit", "Edit Night"]].map(([val, label]) => (
          <button key={val} onClick={() => { setMode(val); if (val !== "single" && editingId) cancel(); }} data-testid={`results-mode-${val}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${mode === val ? "bg-gold text-navy border-gold" : "border-slate-700 text-slate-300 hover:border-gold"}`}>{label}</button>
        ))}
      </div>
      {mode === "single" && (
        <Panel title={editingId ? "Edit Placement" : "Record Placement"} form={
          <form onSubmit={submit} className="space-y-3" data-testid="result-form">
            <Field label="Player Name" required value={f.player_name} onChange={(e) => setF({ ...f, player_name: e.target.value })} data-testid="result-player" />
            <Field label="Event Title" required value={f.event_title} onChange={(e) => setF({ ...f, event_title: e.target.value })} list="events-list" />
            <datalist id="events-list">{events.map((e) => <option key={e.id} value={e.title} />)}</datalist>
            <Field label="Venue" required value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} placeholder="Melbourne" />
            <Field label="Season" value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })} />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Place" type="number" required value={f.place} onChange={(e) => setPlace(e.target.value)} />
              <Field label="Points" type="number" required value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} />
              <Field label="Payout $" type="number" value={f.payout} onChange={(e) => setF({ ...f, payout: e.target.value })} />
            </div>
            <Field label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={f.qualifier} onChange={(e) => setF({ ...f, qualifier: e.target.checked })} /> Finals qualifier</label>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="result-submit">{editingId ? "Save Changes" : "Add Result"}</Button>
              {editingId && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs" data-testid="result-cancel">Cancel</Button>}
            </div>
            {!editingId && <p className="text-slate-500 text-[11px]">Points auto-fill from your Points Formula by place — override any time. Adding a result instantly updates the leaderboards.</p>}
          </form>
        }>
          <ResultsTable items={items} editingId={editingId} onEdit={startEdit} onDelete={onDelete} />
        </Panel>
      )}
      {mode === "bulk" && (
        <Panel title="Bulk Paste Results" form={
          <form onSubmit={submitBulk} className="space-y-3" data-testid="bulk-result-form">
            <Field label="Event Title" value={bulk.event_title} onChange={(e) => setBulk({ ...bulk, event_title: e.target.value })} list="events-list" />
            <datalist id="events-list">{events.map((e) => <option key={e.id} value={e.title} />)}</datalist>
            <Field label="Venue" required value={bulk.venue} onChange={(e) => setBulk({ ...bulk, venue: e.target.value })} placeholder="Melbourne" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Season" value={bulk.season} onChange={(e) => setBulk({ ...bulk, season: e.target.value })} />
              <Field label="Date" type="date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} />
            </div>
            <div>
              <label className="flex items-center justify-center gap-2 border border-dashed border-slate-600 hover:border-gold text-slate-300 hover:text-gold text-xs uppercase tracking-widest py-3 cursor-pointer transition-colors" data-testid="bulk-csv-label">
                <Upload className="w-4 h-4" /> Import a .csv file
                <input type="file" accept=".csv,.txt" className="hidden" data-testid="bulk-csv-input" onChange={(e) => { if (e.target.files[0]) onCsv(e.target.files[0]); e.target.value = ""; }} />
              </label>
              <p className="text-slate-500 text-[11px] mt-1 text-center">Drops straight into the box below — columns Place/Name/Points/Payout are auto-detected.</p>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Finishing order — one player per line, 1st at top</Label>
              <Textarea value={bulk.text} onChange={(e) => setBulk({ ...bulk, text: e.target.value })} rows={9} placeholder={"Jane Smith\nJohn Doe, 88\nAlex Lee, 76, 500"} className="mt-1 rounded-none bg-navy border-slate-700 text-sm font-mono" data-testid="bulk-textarea" />
              <p className="text-slate-500 text-[11px] mt-1">Optional: add points and payout after a comma — <span className="text-slate-300">Name, Points, Payout</span>. Points auto-fill from your Points Formula by finishing place.</p>
            </div>
            <Button type="submit" className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="bulk-submit">Import {rows.length > 0 ? `${rows.length} result${rows.length === 1 ? "" : "s"}` : "Results"}</Button>
          </form>
        }>
          {rows.length > 0 ? (
            <div className="border border-white/10 overflow-x-auto" data-testid="bulk-preview">
              <table className="w-full text-left text-sm min-w-[420px]">
                <thead className="bg-secondary text-gold text-xs uppercase"><tr><th className="px-4 py-2">Place</th><th className="px-4 py-2">Player</th><th className="px-4 py-2">Pts</th><th className="px-4 py-2">Payout</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.place} className="border-t border-white/5">
                      <td className="px-4 py-2 text-gold">{r.place}</td>
                      <td className="px-4 py-2 text-white">{r.player_name}</td>
                      <td className="px-4 py-2 text-slate-300">{r.points}</td>
                      <td className="px-4 py-2 text-slate-400">{r.payout ? `$${r.payout}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-slate-500 text-center py-12">Paste your finishing order or import a CSV to preview it here.</p>}
        </Panel>
      )}
      {mode === "edit" && <BulkEdit items={items} onBulkUpdate={onBulkUpdate} onDelete={onDelete} />}
    </div>
  );
}
